import type {
  AutoRuleListItem,
  AutoRuleMutationResult,
  AutoRuleOpLogItem,
  AutoRuleOpLogParams,
  AutoRuleOpLogResponseItem,
  AutoRulePageResponse,
  AutoRulePagingResult,
  AutoRuleQuery,
  AutoRuleResponseItem,
  AutoRuleStatusChangeParams,
} from './types'

import type { PagingRequest } from '#/api/type'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'

import {
  getDateTimeRange,
  omitBlankString,
  toAutoRuleMediaPlatform,
  toBackendInt64,
} from './convert'

type AutoRulePageRequest = PagingRequest<{
  condition?: AutoRuleQuery
}>

type AutoRuleOpLogPageRequest = PagingRequest<{
  condition?: AutoRuleOpLogParams
}>

function getAutoRuleRequest(params: AutoRulePageRequest) {
  const updatedAt = getDateTimeRange(params.condition?.updateTimeRange)

  return {
    actionType: params.condition?.actionType,
    adAccountId: omitBlankString(params.condition?.adAccountId),
    adId: omitBlankString(params.condition?.adId),
    adSetId: omitBlankString(params.condition?.adSetId),
    campaignId: omitBlankString(params.condition?.campaignId),
    customerKeyword: omitBlankString(params.condition?.customerKeyword),
    mediaPlatform: toAutoRuleMediaPlatform(params.condition?.mediaPlatform),
    merchantId: toBackendInt64(params.condition?.merchantId),
    monitoringTarget: params.condition?.monitoringTarget,
    ruleKeyword: omitBlankString(params.condition?.ruleKeyword),
    pageIndex: params.pageIndex,
    pageSize: params.pageSize,
    status: params.condition?.status,
    updatedAtEnd: updatedAt.end,
    updatedAtStart: updatedAt.start,
  }
}

function mapAutoRuleItem(item: AutoRuleResponseItem): AutoRuleListItem {
  return {
    config: item.config,
    creatorId: item.creatorId,
    creatorName: item.creatorName,
    customerId: item.customerId,
    customerName: item.customerName,
    id: item.id,
    merchantId: item.merchantId,
    name: item.name,
    status: item.status,
    updatedAt: item.updatedAt,
  }
}

function mapAutoRuleOpLogItem(
  item: AutoRuleOpLogResponseItem,
): AutoRuleOpLogItem {
  return {
    content: item.content,
    createdAt: item.createdAt,
    id: item.id,
    operationType: item.operationType,
    operatorId: item.operatorId,
    operatorName: item.operatorName,
  }
}

function mapMutationResult(result?: AutoRuleMutationResult) {
  return {
    id: result?.id,
    version: result?.version,
  }
}

function getOpLogRequest(params: AutoRuleOpLogPageRequest) {
  const operatedAt = getDateTimeRange(params.condition?.createdTimeRange)

  return {
    operatedAtEnd: operatedAt.end,
    operatedAtStart: operatedAt.start,
    operationType: omitBlankString(params.condition?.operationType),
    pageIndex: params.pageIndex,
    pageSize: params.pageSize,
    ruleId: toBackendInt64(params.condition?.ruleId),
  }
}

export const autoRuleApi = defineApi('operation', ({ prefix }) => {
  /**
   * 查询运营端自动规则列表。
   * POST /gobestads/operation/adsAutoRule/page
   */
  function queryRules(
    params: AutoRulePageRequest,
  ): Promise<AutoRulePagingResult<AutoRuleListItem>> {
    return requestClient
      .post<
        AutoRulePageResponse<AutoRuleResponseItem>
      >(prefix('adsAutoRule/page'), getAutoRuleRequest(params))
      .then((res) => ({
        items: (res?.list ?? []).map((item) => mapAutoRuleItem(item)),
        total: res?.total,
      }))
  }

  /**
   * 停用规则。
   * POST /gobestads/operation/adsAutoRule/disable
   */
  async function pauseRule(params: AutoRuleStatusChangeParams) {
    const result = await requestClient.post<AutoRuleMutationResult>(
      prefix('adsAutoRule/disable'),
      {
        id: toBackendInt64(params.id),
      },
    )

    return mapMutationResult(result)
  }

  /**
   * 启用规则。
   * POST /gobestads/operation/adsAutoRule/enable
   */
  async function enableRule(params: AutoRuleStatusChangeParams) {
    const result = await requestClient.post<AutoRuleMutationResult>(
      prefix('adsAutoRule/enable'),
      {
        id: toBackendInt64(params.id),
      },
    )

    return mapMutationResult(result)
  }

  /**
   * 分页查询规则操作日志。
   * POST /gobestads/operation/adsAutoRule/operationLog/page
   */
  function queryOpLogs(
    params: AutoRuleOpLogPageRequest,
  ): Promise<AutoRulePagingResult<AutoRuleOpLogItem>> {
    return requestClient
      .post<
        AutoRulePageResponse<AutoRuleOpLogResponseItem>
      >(prefix('adsAutoRule/operationLog/page'), getOpLogRequest(params))
      .then((res) => ({
        items: (res?.list ?? []).map((item) => mapAutoRuleOpLogItem(item)),
        total: res?.total,
      }))
  }

  return {
    enableRule,
    pauseRule,
    queryOpLogs,
    queryRules,
  }
})
